import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  type ClientRow,
  type CreateClientPayload,
  useUpdateClient,
} from '@/hooks/data/useClients';
import { useAuth } from '@/hooks/useAuth';
import { isMaskedPhoneClient } from '@/lib/phoneDisplay';

interface Props {
  client: ClientRow;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type EditState = Partial<CreateClientPayload> & { status?: ClientRow['status'] };

function fromClient(c: ClientRow): EditState {
  return {
    title: c.title ?? '',
    firstName: c.first_name ?? '',
    lastName: c.last_name,
    clientType: c.client_type,
    phone: c.phone,
    phone2: c.phone_2 ?? '',
    email: c.email ?? '',
    location: c.location ?? '',
    address: c.address ?? '',
    birthDate: c.birth_date ?? '',
    citizenship: c.citizenship ?? '',
    svNumber: c.sv_number ?? '',
    preferredLanguage: (c.preferred_language ?? null) as CreateClientPayload['preferredLanguage'],
    platform: c.platform ?? '',
    platformCode: c.platform_code ?? '',
    notes1: c.notes_1 ?? '',
    notes2: c.notes_2 ?? '',
    notes3: c.notes_3 ?? '',
    status: c.status,
    doNotCall: c.do_not_call ?? false,
  };
}

export function EditClientDialog({ client, open, onOpenChange }: Props) {
  const { t } = useTranslation();
  const { profile } = useAuth();
  // The "Plattform" tag controls audience visibility (imported cold pool
  // vs normal). Only admins may change it, so a normal user can't
  // accidentally move a client between pools by editing the field.
  const canEditPlatform = profile?.role === 'admin';
  // Seller-rules batch — when the API masked the number for this viewer
  // the input would show bullets; saving would overwrite the real number
  // with the mask, so the field is locked (backend also rejects masked
  // values with 400 masked_phone_rejected).
  const phoneMasked = isMaskedPhoneClient(client.phone);
  // Wire key is `phone2` (toSnake doesn't split digits) even though
  // ClientRow declares phone_2 — read both defensively.
  const phone2Masked = isMaskedPhoneClient(
    (client as { phone2?: string | null }).phone2 ?? client.phone_2,
  );
  const update = useUpdateClient(client.id);
  const [form, setForm] = useState<EditState>(() => fromClient(client));
  const initialRef = useRef<EditState>(fromClient(client));
  const [showExtras, setShowExtras] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      const snap = fromClient(client);
      setForm(snap);
      initialRef.current = snap;
      setErr(null);
    }
  }, [open, client]);

  const set = <K extends keyof EditState>(k: K, v: EditState[K]) =>
    setForm((prev) => ({ ...prev, [k]: v }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    // Send only diff vs. the snapshot taken when the dialog opened. Stops
    // an unrelated re-save from silently nulling notes that were never
    // touched. Plus: if the user actually emptied a Notiz that previously
    // had content, ask for explicit confirmation — accidental deletion was
    // the root cause of „Sve moje Notize su nestale" (Jasko, 17.5.2026).
    const initial = initialRef.current;
    const payload: EditState = {};
    for (const [k, v] of Object.entries(form)) {
      if (v === undefined) continue;
      const initialVal = initial[k as keyof EditState];
      if (typeof v === 'string') {
        const trimmed = v.trim();
        // unchanged → skip entirely
        if (trimmed === (typeof initialVal === 'string' ? initialVal.trim() : initialVal ?? '')) {
          continue;
        }
        // explicit Notiz-deletion guard
        if ((k === 'notes1' || k === 'notes2' || k === 'notes3') && trimmed.length === 0 && typeof initialVal === 'string' && initialVal.trim().length > 0) {
          const ok = window.confirm(
            t(
              'clients.edit.notes_delete_confirm',
              'Möchten Sie diese Notiz wirklich löschen? Der vorherige Text wird in den Verlauf verschoben, aber im aktiven Feld entfernt.',
            ),
          );
          if (!ok) return;
        }
        payload[k as keyof EditState] = (trimmed || null) as never;
      } else if (v !== initialVal) {
        payload[k as keyof EditState] = v as never;
      }
    }
    // Nothing changed → close the dialog without a roundtrip.
    if (Object.keys(payload).length === 0) {
      onOpenChange(false);
      return;
    }
    try {
      await update.mutateAsync(payload);
      onOpenChange(false);
    } catch (cause) {
      setErr((cause as Error).message);
    }
  };

  // batch ZN — edits are now open to every authenticated user; the
  // 30-day lock and post-signature state only restrict ownership
  // takeover, not normal field edits. Audit log records every change
  // with actor + timestamp.
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="font-display">
            {t('clients.edit.title', 'Klient bearbeiten')}
          </DialogTitle>
        </DialogHeader>
        <form className="space-y-4" onSubmit={submit}>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label>{t('clients.fields.title', 'Anrede')} *</Label>
              <Select
                value={form.title ?? ''}
                onChange={(e) => set('title', e.target.value)}
                required
              >
                <option value="" disabled>
                  {t('clients.fields.title_placeholder', '— bitte wählen —')}
                </option>
                <option value="Hr.">{t('clients.fields.title_mr', 'Hr.')}</option>
                <option value="Fr.">{t('clients.fields.title_ms', 'Fr.')}</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('clients.fields.clientType', 'Typ')} *</Label>
              <Select
                value={form.clientType}
                onChange={(e) => set('clientType', e.target.value as ClientRow['client_type'])}
                required
              >
                <option value="kupac">{t('enums.client_type.kupac', 'Käufer')}</option>
                <option value="prodavac">{t('enums.client_type.prodavac', 'Verkäufer')}</option>
                <option value="najmoprimac">{t('enums.client_type.najmoprimac', 'Mieter')}</option>
                <option value="najmodavac">{t('enums.client_type.najmodavac', 'Vermieter')}</option>
                <option value="interessent">{t('enums.client_type.interessent', 'Interessent')}</option>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>{t('clients.fields.firstName', 'Vorname')}</Label>
              <Input
                value={form.firstName ?? ''}
                onChange={(e) => set('firstName', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('clients.fields.lastName', 'Nachname')} *</Label>
              <Input
                value={form.lastName ?? ''}
                onChange={(e) => set('lastName', e.target.value)}
                required
              />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>{t('clients.fields.phone', 'Telefon')} *</Label>
              <Input
                value={form.phone ?? ''}
                onChange={(e) => set('phone', e.target.value)}
                required
                disabled={phoneMasked}
              />
              {phoneMasked && (
                <p className="text-[11px] text-ink-3">
                  {t(
                    'clients.edit.phone_masked_hint',
                    'Nur der zuständige Makler kann die Nummer bearbeiten.',
                  )}
                </p>
              )}
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>{t('clients.fields.phone2', 'Telefon 2')}</Label>
              <Input
                value={form.phone2 ?? ''}
                onChange={(e) => set('phone2', e.target.value)}
                disabled={phone2Masked}
              />
              {phone2Masked && (
                <p className="text-[11px] text-ink-3">
                  {t(
                    'clients.edit.phone_masked_hint',
                    'Nur der zuständige Makler kann die Nummer bearbeiten.',
                  )}
                </p>
              )}
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>{t('clients.fields.email', 'E-Mail')}</Label>
              <Input
                type="email"
                value={form.email ?? ''}
                onChange={(e) => set('email', e.target.value)}
                placeholder="email@example.com"
                autoComplete="email"
                inputMode="email"
              />
            </div>
            <div className="space-y-1.5 col-span-2">
              <Label>{t('clients.fields.location', 'Ort')}</Label>
              <Input
                value={form.location ?? ''}
                onChange={(e) => set('location', e.target.value)}
              />
            </div>
            <div className="space-y-1.5">
              <Label>{t('clients.fields.platform', 'Plattform')}</Label>
              <Input
                value={form.platform ?? ''}
                onChange={(e) => set('platform', e.target.value)}
                disabled={!canEditPlatform}
              />
              {!canEditPlatform && (
                <p className="text-[11px] text-ink-3">
                  {t(
                    'clients.fields.platform_locked',
                    'Steuert die Sichtbarkeit (Anrufliste) — nur Admin kann dies ändern.',
                  )}
                </p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label>{t('clients.fields.platformCode', 'Inserat-Code')}</Label>
              <Input
                value={form.platformCode ?? ''}
                onChange={(e) => set('platformCode', e.target.value)}
              />
            </div>
            <div className="col-span-2 flex items-start gap-2 rounded-md border border-line bg-paper-2 p-3">
              <input
                id="ec-do-not-call"
                type="checkbox"
                checked={form.doNotCall ?? false}
                onChange={(e) => set('doNotCall', e.target.checked)}
                className="mt-0.5 h-4 w-4 accent-[hsl(var(--brand-gold))]"
              />
              <label htmlFor="ec-do-not-call" className="cursor-pointer text-xs leading-snug">
                <span className="font-medium text-ink">
                  {t('clients.fields.do_not_call', 'Telefonische Werbung untersagt (DSGVO)')}
                </span>
                <p className="mt-0.5 text-ink-3">
                  {t(
                    'clients.fields.do_not_call_hint',
                    'Wenn aktiviert: keine Anrufe der KI-Sekretärin, keine SMS, WhatsApp oder E-Mail aus dem Kundendatensatz und keine automatischen Kampagnen. Terminbestätigungen und Vertragsunterlagen gehen weiterhin an den Klienten.',
                  )}
                </p>
              </label>
            </div>
            <div className="space-y-1.5">
              <Label>{t('clients.fields.status', 'Status')}</Label>
              <Select
                value={form.status ?? 'otvoreno'}
                onChange={(e) => set('status', e.target.value as ClientRow['status'])}
              >
                <option value="otvoreno">{t('enums.client_status.otvoreno', 'Offen')}</option>
                <option value="na_cekanju">{t('enums.client_status.na_cekanju', 'Wartend')}</option>
                <option value="potpisan_ugovor">{t('enums.client_status.potpisan_ugovor', 'Vertrag unterschrieben')}</option>
                <option value="odbijeno">{t('enums.client_status.odbijeno', 'Abgelehnt')}</option>
              </Select>
            </div>
          </div>

          {[1, 2, 3].map((n) => {
            const k = `notes${n}` as 'notes1' | 'notes2' | 'notes3';
            return (
              <div key={k} className="space-y-1.5">
                <Label>
                  {t('clients.fields.notes', 'Notizen')} {n}
                </Label>
                <Textarea rows={2} value={form[k] ?? ''} onChange={(e) => set(k, e.target.value)} />
              </div>
            );
          })}

          <button
            type="button"
            onClick={() => setShowExtras((v) => !v)}
            className="text-xs text-ink-3 underline-offset-4 hover:underline"
          >
            {showExtras
              ? t('clients.edit.hide_extras', 'Weitere Felder ausblenden')
              : t('clients.edit.show_extras', 'Weitere Felder einblenden')}
          </button>

          {showExtras && (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 rounded-xl border border-line bg-paper-2 p-3">
              <div className="space-y-1.5 col-span-2">
                <Label>{t('clients.fields.address', 'Wohnadresse')}</Label>
                <Input value={form.address ?? ''} onChange={(e) => set('address', e.target.value)} />
              </div>
              <div className="space-y-1.5">
                <Label>{t('clients.fields.birthDate', 'Geburtsdatum')}</Label>
                <Input
                  type="date"
                  value={form.birthDate ?? ''}
                  onChange={(e) => set('birthDate', e.target.value)}
                />
              </div>
              <div className="space-y-1.5">
                <Label>{t('clients.fields.citizenship', 'Staatsbürgerschaft')}</Label>
                <Input
                  value={form.citizenship ?? ''}
                  onChange={(e) => set('citizenship', e.target.value)}
                />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>{t('clients.fields.svNumber', 'SV-Nummer')}</Label>
                <Input
                  value={form.svNumber ?? ''}
                  onChange={(e) => set('svNumber', e.target.value)}
                />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label>
                  {t('clients.fields.preferredLanguage', 'Bevorzugte Sprache')}
                </Label>
                <select
                  value={form.preferredLanguage ?? ''}
                  onChange={(e) =>
                    set(
                      'preferredLanguage',
                      (e.target.value || null) as CreateClientPayload['preferredLanguage'],
                    )
                  }
                  className="flex h-9 w-full rounded-md border border-line bg-card px-3 py-1 text-sm text-ink shadow-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--brand-gold))]/40"
                >
                  <option value="">{t('clients.fields.preferredLanguage_none', '— Nur Deutsch —')}</option>
                  <option value="de">Deutsch</option>
                  <option value="en">English</option>
                  <option value="fr">Français</option>
                  <option value="it">Italiano</option>
                  <option value="sr">Srpski</option>
                  <option value="bg">Български</option>
                  <option value="ru">Русский</option>
                  <option value="tr">Türkçe</option>
                  <option value="ar">العربية</option>
                  <option value="zh">中文</option>
                </select>
                <p className="text-[11px] text-ink-3">
                  {t(
                    'clients.fields.preferredLanguage_hint',
                    'Beim Unterzeichnen eines Dokuments wird automatisch die deutsche Fassung + diese Übersetzung an den Klienten gemailt (sofern Übersetzung verfügbar).',
                  )}
                </p>
              </div>
            </div>
          )}

          {err && (
            <p className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              {err}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel', 'Abbrechen')}
            </Button>
            <Button type="submit" disabled={update.isPending}>
              {update.isPending ? t('common.saving', 'Speichern …') : t('common.save', 'Speichern')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
